import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Compass,
  MapPin,
  Bell,
  User as UserIcon,
  Star,
  Car,
  Route,
  Settings as SettingsIcon,
  LogIn,
  Shield,
  Users,
  ShieldCheck,
  Calendar as CalendarIcon,
  AlertTriangle,
  LogOut,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";

/**
 * Full-screen glassmorphism navigation overlay (mobile + desktop).
 * Triggered from the BottomNav FAB. Premium dark UI with grid of cards.
 */
export default function FabMenuModal({ open, onClose }) {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const isAdmin = user?.role === "admin";

  // ESC to close + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };
  const soon = (label) => { onClose(); toast.info(`${label} — próximamente`); };

  const handleLogout = async () => {
    onClose();
    try { await logout(); } catch { /* ignored */ }
    navigate("/");
  };

  // Public / shared items
  const publicItems = [
    { key: "explorar", icon: Compass, title: "Explorar", subtitle: "Autos disponibles", onClick: () => go("/search") },
    { key: "guia", icon: Sparkles, title: "Guía RivoNes", subtitle: "Descubre y ahorra", onClick: () => go("/guia") },
    { key: "mapa", icon: MapPin, title: "Mapa", subtitle: "Autos cerca de ti", onClick: () => go("/search?view=map") },
    {
      key: "avisos",
      icon: Bell,
      title: "Avisos",
      subtitle: "Notificaciones y alertas",
      onClick: () => (isAuthenticated ? go("/trips") : go("/login")),
    },
    {
      key: "perfil",
      icon: UserIcon,
      title: "Perfil",
      subtitle: "Tu cuenta y viajes",
      onClick: () => (isAuthenticated ? go("/profile") : go("/login")),
    },
    { key: "como-funciona", icon: Star, title: "Cómo funciona", subtitle: "Guía paso a paso", onClick: () => go("/como-funciona") },
    {
      key: "publicar",
      icon: Car,
      title: "Publicar auto",
      subtitle: "Gana como anfitrión",
      onClick: () => (isAuthenticated ? go("/host/listings?new=1") : go("/login")),
    },
    { key: "ruta", icon: Route, title: "Planear ruta", subtitle: "Diseña tu viaje", onClick: () => soon("Planear ruta") },
    {
      key: "configurar",
      icon: SettingsIcon,
      title: "Configurar",
      subtitle: "Ajustes de cuenta",
      onClick: () => (isAuthenticated ? go("/profile") : go("/login")),
    },
  ];

  // Admin-only quick actions
  const adminItems = [
    { key: "admin-dashboard", icon: Shield, title: "Panel admin", subtitle: "Métricas y resumen", onClick: () => go("/admin") },
    { key: "admin-cuentas", icon: Users, title: "Cuentas", subtitle: "Usuarios y anfitriones", onClick: () => go("/admin?tab=users") },
    { key: "admin-verifs", icon: ShieldCheck, title: "Verificaciones", subtitle: "Documentos pendientes", onClick: () => go("/admin?tab=verifications") },
    { key: "admin-vehicles", icon: Car, title: "Vehículos", subtitle: "Aprobar publicaciones", onClick: () => go("/admin?tab=vehicles") },
    { key: "admin-bookings", icon: CalendarIcon, title: "Reservas", subtitle: "Bookings y disputas", onClick: () => go("/admin?tab=bookings") },
    { key: "admin-disputes", icon: AlertTriangle, title: "Disputas", subtitle: "Reportes pendientes", onClick: () => go("/admin?tab=settings") },
  ];

  const items = isAdmin ? publicItems.slice(0, 4) : publicItems;

  return (
    <div
      className="fixed inset-0 z-[80] fab-modal-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Navegación principal"
      data-testid="fab-menu-modal"
    >
      {/* Backdrop with gradient + blur */}
      <div
        className="absolute inset-0 backdrop-blur-2xl bg-gradient-to-br from-black/95 via-[#0a1629]/90 to-[#04101f]/95"
        onClick={onClose}
      />

      {/* Glow accents */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-blue-600/10 blur-3xl" aria-hidden />

      {/* Content */}
      <div className="relative h-full flex flex-col text-white">
        {/* Header */}
        <header className="flex items-start justify-between px-5 pt-[max(env(safe-area-inset-top),20px)] pb-4 sm:px-8 sm:pt-10">
          <div>
            <h2
              className="font-heading font-bold text-3xl sm:text-4xl tracking-tight"
              data-testid="fab-modal-title"
            >
              ¿A dónde vamos?
            </h2>
            <p className="text-sm text-white/55 mt-1">Elige tu destino</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="fab-modal-close"
            className="w-11 h-11 rounded-full bg-white/10 border border-white/15 hover:bg-white/15 transition flex items-center justify-center backdrop-blur"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 pb-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {items.map(({ key, icon: Icon, title, subtitle, onClick }) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                data-testid={`fab-modal-item-${key}`}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-md p-4 sm:p-5 text-left transition-all duration-200 hover:bg-white/[0.08] hover:border-white/20 hover:scale-[1.04] active:scale-[0.98] overflow-hidden"
              >
                {/* Subtle inner highlight */}
                <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" aria-hidden />
                <span
                  className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/[0.03] border border-white/10 mb-3 group-hover:border-cyan-300/30 transition-colors"
                  aria-hidden
                >
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2} />
                </span>
                <p className="font-semibold text-base sm:text-lg leading-tight">{title}</p>
                <p className="text-xs sm:text-sm text-white/50 mt-0.5 leading-snug">{subtitle}</p>
              </button>
            ))}
          </div>

          {isAdmin && (
            <>
              <div className="flex items-center gap-3 mt-7 mb-3" data-testid="fab-modal-admin-section">
                <span className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 font-semibold">
                  Administración
                </span>
                <span className="flex-1 h-px bg-gradient-to-r from-cyan-400/40 via-white/10 to-transparent" aria-hidden />
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {adminItems.map(({ key, icon: Icon, title, subtitle, onClick }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={onClick}
                    data-testid={`fab-modal-item-${key}`}
                    className="group relative rounded-2xl border border-cyan-300/20 bg-cyan-400/[0.06] backdrop-blur-md p-4 sm:p-5 text-left transition-all duration-200 hover:bg-cyan-400/[0.12] hover:border-cyan-300/40 hover:scale-[1.04] active:scale-[0.98] overflow-hidden"
                  >
                    <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent" aria-hidden />
                    <span
                      className="inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-cyan-300/20 to-blue-500/10 border border-cyan-300/20 mb-3"
                      aria-hidden
                    >
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-200" strokeWidth={2} />
                    </span>
                    <p className="font-semibold text-base sm:text-lg leading-tight">{title}</p>
                    <p className="text-xs sm:text-sm text-white/55 mt-0.5 leading-snug">{subtitle}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-white/10 bg-black/40 backdrop-blur px-4 sm:px-8 py-3 pb-[max(env(safe-area-inset-bottom),12px)]">
          {!isAuthenticated ? (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => go("/login")}
                data-testid="fab-modal-login-btn"
                className="flex-1 h-12 rounded-2xl bg-white/[0.04] border-white/20 text-white hover:bg-white/10 hover:text-white"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Iniciar sesión
              </Button>
              <Button
                onClick={() => go("/login?mode=register")}
                data-testid="fab-modal-register-btn"
                className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white border-0 shadow-[0_8px_32px_rgba(34,211,238,0.35)]"
              >
                Crear cuenta
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-white/70 truncate">
                Hola, <span className="font-semibold text-white">{user?.first_name || user?.name?.split(" ")?.[0] || "amigo"}</span>
                {isAdmin && <span className="ml-2 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">Admin</span>}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="fab-modal-logout-btn"
                  aria-label="Cerrar sesión"
                  className="h-11 w-11 rounded-2xl bg-white/[0.04] border border-white/15 hover:bg-white/10 transition flex items-center justify-center"
                >
                  <LogOut className="w-4 h-4" />
                </button>
                <Button
                  onClick={() => go(isAdmin ? "/admin" : "/profile")}
                  data-testid="fab-modal-profile-btn"
                  className="h-11 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white border-0 px-5 shadow-[0_6px_22px_rgba(34,211,238,0.35)]"
                >
                  {isAdmin ? "Ir al panel admin" : "Ir a mi cuenta"}
                </Button>
              </div>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
